import React from "react";
import { LegalLayout, Section } from "@/components/legal/LegalLayout";

const P: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-[color:var(--sg-fg)] leading-relaxed">{children}</p>
);
const UL: React.FC<{ items: string[] }> = ({ items }) => (
  <ul className="list-disc ml-6 space-y-1.5 text-[color:var(--sg-fg)]">
    {items.map((it, i) => <li key={i}>{it}</li>)}
  </ul>
);

export const TermsOfService: React.FC = () => (
  <LegalLayout
    testId="page-terms"
    title="Terms of Service"
    subtitle="The agreement between you and SociBaba governing use of the platform."
    lastUpdated="February 13, 2026"
    seoPath="/terms"
    seoDescription="SociBaba Terms of Service — accounts, acceptable use, subscriptions, IP, liability, and governing law."
    seoKeywords={["SociBaba terms","terms of service","acceptable use","subscription agreement"]}
  >
    <P>
      These Terms of Service ("Terms") govern your access to and use of SociBaba ("SociBaba", "we", "our"). By creating an account or using the service you agree to these Terms.
    </P>

    <Section n={1} title="Accounts">
      <UL items={[
        "You must be at least 18 years old (or 13 with parental/guardian consent) to use SociBaba.",
        "You are responsible for keeping your credentials confidential.",
        "You are responsible for all activity under your workspace.",
      ]} />
    </Section>

    <Section n={2} title="Acceptable use">
      <P>You agree not to:</P>
      <UL items={[
        "Use SociBaba to publish content that is illegal, hateful, harassing, defamatory, or infringing.",
        "Use SociBaba to spam, scrape non-public data, evade platform rate-limits, or violate the terms of any connected platform (YouTube, Meta, etc.).",
        "Reverse-engineer, tamper with, or attempt to disrupt the service.",
        "Resell the service without our written permission.",
      ]} />
      <P>We may suspend or terminate accounts that violate these rules.</P>
    </Section>

    <Section n={3} title="Content ownership">
      <P>
        You retain ownership of your content. By uploading content to SociBaba you grant us a limited, worldwide licence to store, process, and transmit that content solely to provide the service (e.g. publish it to your connected accounts, generate thumbnails, run analysis).
      </P>
    </Section>

    <Section n={4} title="AI-generated content">
      <P>
        Reply drafts, opportunity summaries, and other AI-produced outputs are generated by third-party foundation models. You are responsible for reviewing and approving any AI output before it is published to any external platform. We do not warrant that AI outputs are accurate, factual, or free from bias.
      </P>
    </Section>

    <Section n={5} title="Third-party platforms">
      <P>
        SociBaba connects to platforms including YouTube, Facebook, and Instagram via their official APIs. Your use of those platforms remains subject to their own terms. We are not responsible for outages, policy changes, or account actions taken by those platforms.
      </P>
    </Section>

    <Section n={6} title="Subscriptions and payments">
      <UL items={[
        "Paid plans are billed monthly or annually in advance. Prices are shown at checkout.",
        "You may cancel at any time; access continues until the end of the paid period.",
        "See our Refund Policy for refund terms.",
      ]} />
    </Section>

    <Section n={7} title="Service availability">
      <P>
        We aim for high uptime but do not guarantee uninterrupted service. Scheduled maintenance and force-majeure events may cause downtime.
      </P>
    </Section>

    <Section n={8} title="Warranty disclaimer">
      <P>
        SociBaba is provided "as is" without warranties of any kind, express or implied, to the maximum extent permitted by law.
      </P>
    </Section>

    <Section n={9} title="Limitation of liability">
      <P>
        To the maximum extent permitted by law, SociBaba's total aggregate liability under these Terms is limited to the amount you paid us in the twelve months preceding the claim. We are not liable for indirect, incidental, or consequential damages.
      </P>
    </Section>

    <Section n={10} title="Termination">
      <P>
        You may terminate at any time by deleting your workspace. We may terminate for material breach of these Terms.
      </P>
    </Section>

    <Section n={11} title="Governing law">
      <P>
        These Terms are governed by the laws of India. Disputes will be resolved in the courts of Bengaluru, Karnataka.
      </P>
    </Section>

    <Section n={12} title="Contact">
      <P>Email <a className="text-[color:var(--sg-primary)] hover:underline" href="mailto:legal@socibaba.com">legal@socibaba.com</a> with any questions.</P>
    </Section>
  </LegalLayout>
);

export default TermsOfService;
